import { Icon } from '@/components/ui/Icon';
import {
  mdiAlertCircle,
  mdiCancel,
  mdiCheck,
  mdiCheckCircle,
  mdiClock,
  mdiPause,
  mdiPlay,
} from '@mdi/js';
import * as React from 'react';
import type { ImportJobProgress } from '../hooks/useImportJobs';

interface JobHistoryProps {
  jobs: ImportJobProgress[];
  onSelectJob: (jobId: number) => void;
  selectedJobId: number | null;
}

export const JobHistory: React.FC<JobHistoryProps> = ({
  jobs,
  onSelectJob,
  selectedJobId,
}) => {
  if (jobs.length === 0) {
    return null;
  }

  const getStatusIcon = (status: ImportJobProgress['status']) => {
    switch (status) {
      case 'processing':
        return { icon: mdiPlay, color: 'text-blue-500' };
      case 'pending':
        return { icon: mdiClock, color: 'text-grey-500' };
      case 'paused':
        return { icon: mdiPause, color: 'text-yellow-500' };
      case 'completed':
        return { icon: mdiCheckCircle, color: 'text-green-500' };
      case 'failed':
        return { icon: mdiAlertCircle, color: 'text-red-500' };
      case 'cancelled':
        return { icon: mdiCancel, color: 'text-grey-500' };
      default:
        return { icon: mdiCheck, color: 'text-grey-500' };
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className='mt-6'>
      <h3 className='text-lg font-medium text-grey-900 mb-3'>Recent Jobs</h3>
      <div className='space-y-2'>
        {jobs.slice(0, 10).map(job => {
          const { icon, color } = getStatusIcon(job.status);
          const isSelected = selectedJobId === job.id;

          return (
            <div
              key={job.id}
              onClick={() => onSelectJob(job.id)}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-grey-200 hover:border-grey-300'
              }`}
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-3'>
                  <Icon path={icon} size={20} className={color} />
                  <div>
                    <p className='font-medium text-grey-900'>
                      Job #{job.id}
                      <span className='ml-2 text-sm text-grey-500'>
                        (
                        {job.provider === 'google_maps'
                          ? 'Google Maps'
                          : job.provider}
                        )
                      </span>
                    </p>
                    <p className='text-xs text-grey-500'>
                      {formatDate(job.createdAt)}
                    </p>
                  </div>
                </div>
                <div className='text-right'>
                  <p className='text-sm font-medium text-grey-900'>
                    {job.processedItems} / {job.totalItems}
                  </p>
                  <p className='text-xs text-grey-500'>
                    <span className='text-green-600'>
                      {job.successfulItems}
                    </span>
                    {' / '}
                    <span className='text-red-600'>{job.failedItems}</span>
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
